/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-17 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { DEFAULT_SEARCH_PANEL_BLOCK } from 'src/constants/search-page-display';
import { checkHasSpecialCharacters } from 'src/helpers';
import { decodeHtmlCode, encodeHtmlCode } from './index';

/**
 * Build initial data for search page display
 *
 * @param  {Object} filterSettings filter settings from API
 *
 * @return {Object} initialValue
 */
export const buildInitialSearchPageDisplayValues = (filterSettings: Object, i18n: Object) => {
  let result = JSON.parse(JSON.stringify(DEFAULT_SEARCH_PANEL_BLOCK));

  const searchPanelBlocks = _.get(filterSettings, 'search.searchPanelBlocks', {});
  // Reuse existed value of resultEmpty if any
  // const resultEmpty = _.get(filterSettings, 'label.resultEmpty');
  // _.set(searchPanelBlocks, 'searchEmptyResultMessages.label', resultEmpty);

  // Update default value with setting from API
  // Search panel block is an object with 2 levels
  _.map(result, (itemLv1, keyLv1) => {
    _.map(itemLv1, (itemLv2, keyLv2) => {
      const additionalParams = {};
      if (_.isString(itemLv2) && itemLv2.includes('searchPanelBlock_NoResultMessage')) {
        additionalParams.terms = '{{ terms }}';
      }

      const defaultValue =
        ['label', 'searchTips'].includes(keyLv2) && _.includes(itemLv2, 'AdminHTML')
          ? i18n.translate(itemLv2, additionalParams)
          : itemLv2;
      let value = _.get(searchPanelBlocks, `${keyLv1}.${keyLv2}`, defaultValue);

      if (value && _.isString(value)) {
        value = value.replace(/{{ breakline }}/g, '\n');
        value = decodeHtmlCode(value);
      }

      result[keyLv1][keyLv2] = value;
    });
  });

  return result;
};

/**
 * Build data for saving filter settings
 *
 * @param  {Object} data search page display data
 * @param  {Object} filterSettings filter settings from API
 *
 * @return {Object} value value for sending API
 */
export const buildSearchPageDisplayValues = (
  data: Object,
  filterSettings: Object,
  primaryLocale: string
) => {
  const searchPanelBlocks = _.cloneDeep(_.get(filterSettings, 'search.searchPanelBlocks', {}));
  _.mergeWith(searchPanelBlocks, data, (objectValue, sourceValue) => {
    if (_.isArray(objectValue) || _.isArray(sourceValue)) {
      return sourceValue || objectValue;
    }
  });
  // Save suggestion block labels as translation values (only apply to primary locale)
  const updatedFilterSettings = saveSearchPageDisplayToTranslation(
    searchPanelBlocks,
    filterSettings,
    primaryLocale
  );

  const newSettings = _.cloneDeep(updatedFilterSettings);
  _.set(newSettings, 'search.searchPanelBlocks', searchPanelBlocks);

  let resultEmpty = _.get(searchPanelBlocks, 'searchEmptyResultMessages.label', '');
  if (resultEmpty) {
    resultEmpty = resultEmpty.replace(/\n/g, '{{ breakline }}');
    _.set(newSettings, 'search.searchPanelBlocks.searchEmptyResultMessages.label', resultEmpty);
  }

  let searchTipsContent = _.get(searchPanelBlocks, 'searchTips.searchTips', '');
  if (searchTipsContent) {
    searchTipsContent = searchTipsContent.replace(/\n/g, '{{ breakline }}');
    _.set(newSettings, 'search.searchPanelBlocks.searchTips.searchTips', searchTipsContent);
  }

  // Build mapping variants if suggestion product has id who is variant id
  // => build field variants
  const variants = [];
  const products = _.get(searchPanelBlocks, 'mostPopularProducts.productList', []);
  products.forEach(product => {
    if (product.product_id && product.id !== product.product_id) {
      variants.push({ productId: product.product_id, variantId: product.id });
    }
  });

  // Create mapping field. It is used to switch back to original product if turn off Variants display
  if (variants.length > 0) {
    _.set(newSettings, 'search.searchPanelBlocks.mostPopularProducts.variants', variants);
  }

  return newSettings;
};

/**
 * Check validate search page display
 *
 * @param  {Object} data search page display data
 *
 * @return {string}           validate string
 */
export const checkValidateSearchPageDisplay = (data: Object) => {
  const {
    products = {},
    collections = {},
    pages = {},
    searchEmptyResultMessages = {},
    searchTips = {},
    searchTermSuggestions = {},
    mostPopularProducts = {}
  } = data;

  // Products
  if (products.label.length > 128) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_Products_Label_Length';
  }

  let checkSpecialCharactersValue = checkHasSpecialCharacters(products.label, '&()-\\\\\\s', true);
  if (_.isEmpty(products.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_Products_Label_SpecialCharacter';
  }

  if (parseInt(products.pageSize) === 0) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_Products_PageSize';
  }
  // Collections
  if (collections.label.length > 128) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_Collections_Label_Length';
  }

  checkSpecialCharactersValue = checkHasSpecialCharacters(collections.label, '&()-\\\\\\s', true);
  if (_.isEmpty(collections.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_Collections_Label_SpecialCharacter';
  }

  if (parseInt(collections.pageSize) === 0) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_Collections_PageSize';
  }

  // Pages
  if (pages.label.length > 128) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_BlogsPages_Label_Length';
  }

  checkSpecialCharactersValue = checkHasSpecialCharacters(pages.label, '&()-\\\\\\s', true);
  if (_.isEmpty(pages.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_BlogsPages_Label_SpecialCharacter';
  }

  if (parseInt(pages.pageSize) === 0) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_BlogsPages_PageSize';
  }

  // no result messages
  if (searchEmptyResultMessages.label.length > 156) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_NoResultMessage_Label_Length';
  }
  checkSpecialCharactersValue = checkHasSpecialCharacters(
    searchEmptyResultMessages.label,
    '&()-{}\\\\\\s',
    true
  );
  if (_.isEmpty(searchEmptyResultMessages.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_NoResultMessage_Label_SpecialCharacter';
  }

  // search tips
  if (searchTips.label.length > 128) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_SearchTips_Label_Length';
  }
  checkSpecialCharactersValue = checkHasSpecialCharacters(searchTips.label, '&()-\\\\\\s', true);

  if (_.isEmpty(searchTips.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_SearchTips_Label_SpecialCharacter';
  }

  if (searchTips.searchTips.length > 400) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_SearchTips_Value_Length';
  }
  checkSpecialCharactersValue = checkHasSpecialCharacters(
    searchTips.searchTips,
    '&()-\\\\\\s',
    true
  );
  if (_.isEmpty(searchTips.searchTips.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_SearchTips_Value_SpecialCharacter';
  }

  // search term suggestions
  if (searchTermSuggestions.label.length > 128) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_SearchTermSuggestions_Label_Length';
  }
  checkSpecialCharactersValue = checkHasSpecialCharacters(
    searchTermSuggestions.label,
    '&()-\\\\\\s',
    true
  );
  if (_.isEmpty(searchTermSuggestions.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_SearchTermSuggestions_Label_SpecialCharacter';
  }

  // product suggestions
  if (mostPopularProducts.label.length > 128) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_ProductSuggestions_Label_Length';
  }
  checkSpecialCharactersValue = checkHasSpecialCharacters(
    mostPopularProducts.label,
    '&()-\\\\\\s',
    true
  );
  if (_.isEmpty(mostPopularProducts.label.trim()) || checkSpecialCharactersValue) {
    return 'AdminHTML.SearchPageDisplay.Validate.invalid_ProductSuggestions_Label_SpecialCharacter';
  }

  return '';
};

/**
 * Save search page display label to translation values as primary locale
 *
 * @param  {object} searchPanelBlocks
 * @param  {object} filterSettings
 * @param  {string} primaryLocale
 *
 * @return {object}                  updated filter settings
 */
const saveSearchPageDisplayToTranslation = (searchPanelBlocks, filterSettings, primaryLocale) => {
  let { label = {}, labelTranslations = {} } = filterSettings;

  const types = {
    searchPanelCollection: 'collections.label',
    searchPanelPage: 'pages.label',
    searchPanelProduct: 'products.label',
    resultEmpty: 'searchEmptyResultMessages.label', // existed field in translation tool
    searchTipsTitle: 'searchTips.label',
    searchTipsContent: 'searchTips.searchTips',
    searchTermSuggestions: 'searchTermSuggestions.label',
    mostPopularProducts: 'mostPopularProducts.label',
    noSearchResultProductsLabel: 'mostPopularProducts.label',
    noSearchResultSearchTermLabel: 'searchTermSuggestions.label'
  };

  // Update translation label
  for (let key in types) {
    // skip loop if the property is from prototype
    if (!types.hasOwnProperty(key)) continue;

    const keyPath = types[key];
    if (!_.isEmpty(keyPath)) {
      let searchPanelLabel = _.get(searchPanelBlocks, keyPath, '');
      searchPanelLabel = searchPanelLabel.replace(/\n/g, '{{ breakline }}');
      searchPanelLabel = encodeHtmlCode(searchPanelLabel);
      _.set(label, key, searchPanelLabel);
      _.set(labelTranslations, `${primaryLocale}.${key}`, searchPanelLabel);
    }
  }

  return {
    ...filterSettings,
    label,
    labelTranslations
  };
};

/**
 * check validate search term suggestion
 * @param {*} searchTermSuggestion
 * @param {*} searchTermSuggestionsList
 * @param {*} i18n
 * @returns
 */
export const checkValidateSearchTermSuggestion = (
  searchTermSuggestion: Object,
  searchTermSuggestionsList: Array<Object>,
  i18n: Object
) => {
  let value = searchTermSuggestion.trim().toLowerCase();

  if (value) {
    if (value.length > 128) {
      return {
        error: i18n.translate(
          'AdminHTML.SearchPageDisplay.Helper.searchPageDisplayHelper_CheckValidateSearchTermSuggestion_Error1'
        )
      };
    }

    let checkSpecialCharactersValue = checkHasSpecialCharacters(value);
    if (_.isEmpty(value) || checkSpecialCharactersValue) {
      return {
        error: i18n.translate(
          'AdminHTML.SearchPageDisplay.Helper.searchPageDisplayHelper_CheckValidateSearchTermSuggestion_Error2'
        )
      };
    }

    const found = _.find(searchTermSuggestionsList, item => item === value);
    if (_.isEmpty(found)) {
      return {
        searchTermSuggestion: value,
        error: ''
      };
    } else {
      return {
        error: i18n.translate(
          'AdminHTML.SearchPageDisplay.Helper.searchPageDisplayHelper_CheckValidateSearchTermSuggestion_Error3'
        )
      };
    }
  }

  return {};
};
